/**
 * TherapyPhoto — foto de la terapia en su marco, o la ilustración de respaldo
 * (CEC-261012-58fb).
 *
 * La usan las tarjetas del Inicio y de /terapias y el bloque "Terapia
 * destacada". Quien la usa pone la forma del marco en `className` (cuadro en
 * las tarjetas, columna completa en la destacada). La foto se recorta para
 * llenar el marco, como en las tarjetas de productos y obras del Inicio.
 */
import type { ReactNode } from 'react';

import { Image } from '../../components/ui/Image';
import { cn } from '../../lib/cn';

/** Solo direcciones completas: una ruta suelta (`therapies/foto.webp`) que
 *  quedó de datos de prueba daría una imagen rota. Mismo criterio que la
 *  galería del Inicio (`isResolvableImageUrl` en `pages/Home.tsx`). */
function hasTherapyPhoto(coverUrl: string | null | undefined): coverUrl is string {
  return Boolean(coverUrl) && /^(https?:\/\/|data:)/i.test(coverUrl as string);
}

export interface TherapyPhotoProps {
  coverUrl: string | null | undefined;
  /** Nombre de la terapia: texto alternativo de la foto. */
  title: string;
  /** Lo que se ve cuando no hay foto (la ilustración de siempre). */
  fallback: ReactNode;
  /** Forma y tamaño del marco (por ejemplo `aspect-square w-full`). */
  className?: string;
}

export function TherapyPhoto({ coverUrl, title, fallback, className }: TherapyPhotoProps) {
  return (
    <div data-therapy-photo="" className={cn('relative overflow-hidden', className)}>
      {hasTherapyPhoto(coverUrl) ? (
        <Image
          src={coverUrl}
          alt={title}
          width={600}
          height={600}
          fit="cover"
          className="absolute inset-0 h-full w-full transition-transform duration-3 ease-out group-hover:scale-105"
        />
      ) : (
        fallback
      )}
    </div>
  );
}
